import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { basicService, kaseyaOneService, sessionService } from '@app/services';
import { setExceptionHandler, signInFailure } from '@app/store/actions';
import { PageLoader } from '@app/components';
import { AuthInfo, Session, TranslationsTypes } from '@app/models';
import { TwoFactorList, SetUpDeviceU2f } from '@app/modules/mfaToken/components';
import { u2fSignIn } from '@app/modules/mfaToken/helpers';
import { loginForms } from '@app/enums';
import { SelectAnotherProvider } from '@app/components';
import LoginForm from './login-form/login-form.component';
import {
    SignInUnknown,
    SignInList,
    SignInVerfiedList,
    SignInSelected,
    SignInNoUserMfaForApp,
    SignInTwoFactorOtp,
    SignInTwoFactorPush,
    SignInTwoFactorUnsupported,
    SignInElevate,
    SignInKaseyaOneCreds
} from './components';
import { IGlobalState } from '@app/models';
import {
    getUrlParam,
    handleKaseyaOneSignin,
    K1_LOGIN_CODE,
    localStore,
    RETURN_KASEYA_ONE_STATE,
} from '@app/helpers';
import ResetPasswordSelfservicePage from '@app/pages/login/resetPasswordSelfservice.page';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { AxiosError, AxiosResponse } from 'axios';

const LoginPage = () => {
    const shellConfigLoading = useSelector((state: IGlobalState) => state.basic.shellConfigLoading);
    const redirectAfterLogin: string = useSelector((state: IGlobalState) => state.launch.redirectAfterSignin);
    const currentYear = new Date().getFullYear();
    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const [loginForm, setLoginForm] = useState<loginForms>(loginForms.unknown);
    const [authInfo, setAuthInfo] = useState<AuthInfo>({ providers: [], contextToken: '' });
    const [redirectUsername] = useState<string>(tryDetectUsername());
    const [rememberMe, setRememberMe] = useState<boolean>(false);
    const [isKaseyaOneEnabled, setIsKaseyaOneEnabled] = useState(false);
    const [errorKaseyaOneCode, setErrorKaseyaOneCode] = useState<TranslationsTypes>('');
    const { error } = { error: '' };
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const queryCodeString = basicService.getQueryVariable('code');
    const queryStateString = basicService.getQueryVariable('state');
    const queryErrorString = basicService.getQueryVariable('error');
    const queryLoginHintString = basicService.getQueryVariable('login_hint');
    const [userSessionInfo, setUserSessionInfo] = useState({
        displayInfo: '',
        username: '',
        userUuid: ''
    });

    useEffect(() => {
        // test if the user is onboarding here.
        const obtToken = basicService.getQueryVariable('obt');

        if (obtToken !== '') {
            basicService.redirect('/onboard/' + obtToken);
            return;
        }

        // app launch elevate
        const hasMfaParam = basicService.getQueryVariable('mfa') === 'req';
        if (hasMfaParam) {
            setLoginForm(loginForms.elevate);
            return;
        }

        if (sessionService.isUserHaveSessions() && redirectUsername === '') {
            const sessions = sessionService.getSessionHistory() as Session[];
            if (sessions.length === 1) {
                setLoginForm(loginForms.selected);
                setUserSessionInfo({
                    displayInfo: sessions[0].displayName,
                    username: sessions[0].username,
                    userUuid: sessions[0].userUuid
                });
            } else {
                setLoginForm(loginForms.list);
            }
        }
    }, []);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
    }, []);

    const handleGetRedirectKaseyaOneUrlSuccess = (response: AxiosResponse) => {
        const redirectUrl: string = response.data?.url;

        if (redirectUrl) {
            setIsLoading(false);
            window.location.href = redirectUrl;
        }
    };

    const handleError = (error: AxiosError<any>) => {
        setIsLoading(false);
        setErrorKaseyaOneCode(error.response?.data.error);
    };

    const handleKaseyaOneLoginError = (error: AxiosError<any>) => {
        setIsLoading(false);
        setIsPageLoading(false);
        dispatch(signInFailure(error.response?.data.error));
        setErrorKaseyaOneCode(error.response?.data.error);
    };

    const handleKaseyaOneGetStatusSuccess = (response: AxiosResponse) => {
        const enabled: boolean = response.data?.enabled;

        setIsKaseyaOneEnabled(enabled);
    };

    const handleKaseyaOneClick = () => {
        setIsLoading(true);
        kaseyaOneService.getRedirectLoginUrl('', handleError, handleGetRedirectKaseyaOneUrlSuccess);
    };

    const handleKaseyaOneSigninForNotLinkedUser = (username: string, password: string) => {
        setIsLoading(true);
        handleKaseyaOneSignin({ username, password, setErrorKaseyaOneCode, setIsPageLoading, setIsLoading, setLoginForm, dispatch });
    };

    useEffect(() => {
        kaseyaOneService.getKaseyaOneOrgStatus(handleError, handleKaseyaOneGetStatusSuccess);
    }, []);

    const kaseyaOneLoginSuccess = () => {
        setIsPageLoading(true);
        handleKaseyaOneSignin({ setErrorKaseyaOneCode, setIsPageLoading, setIsLoading, dispatch, setLoginForm });
    };

    const kaseyaOneErrorSuccess = () => {
        setIsLoading(false);
        setIsPageLoading(false);
        setErrorKaseyaOneCode('KASEYA_ONE_LOGIN_ERROR' as TranslationsTypes);
    };

    useEffect(() => {
        const savedQueryCodeString = localStore.getItem(K1_LOGIN_CODE);

        if (queryStateString === RETURN_KASEYA_ONE_STATE && queryCodeString && savedQueryCodeString !== queryCodeString) {
            setIsPageLoading(true);
            localStore.setItem(K1_LOGIN_CODE, queryCodeString);
            kaseyaOneService.loginKaseyaOne({ code: queryCodeString }, handleKaseyaOneLoginError, kaseyaOneLoginSuccess);
        } else if (queryStateString === RETURN_KASEYA_ONE_STATE && queryErrorString) {
            localStore.setItem(RETURN_KASEYA_ONE_STATE, queryCodeString);
            kaseyaOneService.loginKaseyaOne({ error: queryErrorString }, handleKaseyaOneLoginError, kaseyaOneErrorSuccess);
        }
    }, []);

    useEffect(() => {
        if (queryLoginHintString) {
            setIsPageLoading(true);
            kaseyaOneService.getRedirectLoginUrl(queryLoginHintString, handleError, handleGetRedirectKaseyaOneUrlSuccess);
        }
    }, []);

    useEffect(() => {
        if (error) {
            const errorMessage = translation(error as TranslationsTypes);

            dispatch(handleSetSnackbar({ show: true, message: errorMessage, severity: 'error' }));
        }
    }, [error]);

    const formSwith = (form: loginForms): JSX.Element => {
        switch (form) {
            case loginForms.unknown:
                return (
                    <SignInUnknown
                        setLoginForm={setLoginForm}
                        setAuthInfo={setAuthInfo}
                        usernameHint={redirectUsername}
                        setRememberMe={setRememberMe}
                        rememberMe={rememberMe}
                        isKaseyaOneLoginEnabled={isKaseyaOneEnabled}
                        handleKaseyaOneClick={handleKaseyaOneClick}
                        errorKaseyaOneCode={errorKaseyaOneCode}
                        isLoading={isLoading}
                    />
                );
            case loginForms.list:
                return (
                    <SignInList
                        setLoginForm={setLoginForm}
                        setUserSessionInfo={setUserSessionInfo}
                        isKaseyaOneLoginEnabled={isKaseyaOneEnabled}
                        handleKaseyaOneClick={handleKaseyaOneClick}
                        errorKaseyaOneCode={errorKaseyaOneCode}
                        isLoading={isLoading}
                    />
                );
            case loginForms.verifiedList:
                return <SignInVerfiedList />;
            case loginForms.selected:
                return (
                    <SignInSelected
                        setLoginForm={setLoginForm}
                        userSessionInfo={userSessionInfo}
                        setAuthInfo={setAuthInfo}
                        isKaseyaOneLoginEnabled={isKaseyaOneEnabled}
                        handleKaseyaOneClick={handleKaseyaOneClick}
                        errorKaseyaOneCode={errorKaseyaOneCode}
                        isLoading={isLoading}
                    />
                );
            case loginForms.noUserMfaForApp:
                return <SignInNoUserMfaForApp />;
            case loginForms.twoFactorOtp:
                return <SignInTwoFactorOtp setLoginForm={setLoginForm} rememberMe={rememberMe} />;
            case loginForms.twoFactorPush:
                return <SignInTwoFactorPush setLoginForm={setLoginForm} />;
            case loginForms.twoFactorU2F:
                return (
                    <SetUpDeviceU2f
                        bottomButton={<SelectAnotherProvider setLoginForm={setLoginForm} />}
                        registrationCallback={u2fSignIn(dispatch, authInfo.contextToken)}
                        setLoginForm={setLoginForm}
                    />
                );

            case loginForms.twoFactorUnsupported:
                return <SignInTwoFactorUnsupported />;
            case loginForms.twoFactorList:
                return <TwoFactorList setLoginForm={setLoginForm} providers={authInfo.providers} contextToken={authInfo.contextToken} />;
            case loginForms.elevate:
                return <SignInElevate setLoginForm={setLoginForm} userSessionInfo={userSessionInfo} setAuthInfo={setAuthInfo} />;
            case loginForms.resetPasswordValidate:
                return <ResetPasswordSelfservicePage setLoginForm={setLoginForm} />;
            case loginForms.credsKaseyaOne:
                return (
                    <SignInKaseyaOneCreds
                        signinForNotLinkedUser={handleKaseyaOneSigninForNotLinkedUser}
                        isLoading={isLoading}
                        errorKaseyaOneCode={errorKaseyaOneCode}
                    />
                );
            default:
                return <LoginForm />;
        }
    };

    function tryDetectUsername(): string {
        if (redirectAfterLogin !== undefined && redirectAfterLogin !== '') {
            let hint: string = getUrlParam(redirectAfterLogin, 'login_hint');

            if (hint === '') {
                hint = getUrlParam(redirectAfterLogin, 'username');
            }

            return hint;
        }

        return '';
    }

    return (
        <div className='login'>
            {(shellConfigLoading || isPageLoading) && <PageLoader />}
            {!shellConfigLoading && !isPageLoading && (
                <>
                    <div className='login__logo--left logo__kaseya' />
                    <div className='login__wrapper'>
                        <div className='login__container'>
                            <div className='login__form'>{formSwith(loginForm)}</div>
                        </div>
                        <div className='login__footer'>
                            <span className='login__footer--left'>
                                <a href={basicService.getHelpUrl()} title={translation('HELP_AND_SUPPORT')} target='_blank' rel='noopener noreferrer'>
                                    {translation('HELP_AND_SUPPORT')}
                                </a>
                            </span>
                            <span className='login__footer--right'>
                                &nbsp;| &copy;
                                {`${translation('COPYRIGHT')} ${currentYear}`}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LoginPage;
